import React, { useEffect, useState } from "react";
import AppContainer from "../../common/app_container";
import { useLicensablePropertyAPI } from "@src/api/licensable_property_api";
import { LicensableProperty } from "@src/model/licensable_property";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm, useFieldArray } from "react-hook-form";
import Alert, { AlertProps } from "@src/common/alert";
import BackButton from "@src/common/components/back-button";

const LicensablePropertyEdit: React.FC = () => {
    const { id } = useParams();
    const propertyAPI = useLicensablePropertyAPI();
    const navigate = useNavigate();
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);

    const yearOptions = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 225 }, (_, i) => currentYear + 5 - i);
    }, []);

    const [loadedProperty, setLoadedProperty] = useState<LicensableProperty | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        control
    } = useForm<LicensableProperty>({
        defaultValues: {
            primary_professions: "",
            profile_image: "",
            hero_image: ""
        }
    });

    const { fields: socialFields, append: appendSocial, remove: removeSocial } = useFieldArray({
        control,
        name: "social_accounts"
    });

    const { fields: characteristicFields, append: appendCharacteristic, remove: removeCharacteristic } = useFieldArray({
        control,
        name: "characteristics"
    });

    const { fields: priceFields, append: appendPrice, remove: removePrice } = useFieldArray({
        control,
        name: "prices"
    });

    const onSubmit: SubmitHandler<LicensableProperty> = async (data) => {
        setIsSaving(true);
        setStatus(null);
        try {
            const propertyToSave = { ...(loadedProperty || {}), ...data };
            const savePromise = id
                ? propertyAPI.updateLicensableProperty({ ...propertyToSave, id })
                : propertyAPI.createLicensableProperty(propertyToSave);

            await savePromise;
            setIsSaving(false);
            setStatus({ type: "success", message: "Property saved successfully" });
            setTimeout(() => {
                navigate("/licensable-properties");
            }, 2000);
        } catch (err: any) {
            setIsSaving(false);
            setStatus({ type: "error", message: "Error saving property: " + err.message });
        }
    };

    useEffect(() => {
        if (id) {
            propertyAPI.getById(id)
                .then((response) => {
                    setLoadedProperty(response);
                    reset(response);
                })
                .catch((err) => {
                    setStatus({ type: "error", message: "Error loading property: " + err.message });
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <AppContainer>
            <div className="container md:p-6">
                <BackButton variant="ghost" onClick={() => navigate("/licensable-properties")} />
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">{id ? "Edit Licensable Property" : "Add Licensable Property"}</h1>
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow disabled:opacity-50 transition duration-150 ease-in-out"
                        type="submit"
                        form="property_form"
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </div>

                {status && (
                    <div className="mb-6">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} id="property_form" className="bg-white shadow rounded-lg p-6 space-y-6">

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Name
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("name", { required: "Name is required" })}
                                    type="text"
                                    id="name"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="Property Name"
                                />
                            </div>
                            {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name.message}</p>}
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="property_type" className="block text-sm font-medium text-gray-700">
                                Property Type
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("property_type")}
                                    id="property_type"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">Select Type</option>
                                    <option value="real_person">Real Person</option>
                                    <option value="generative_model">Generative Model</option>
                                    <option value="fictional_character">Fictional Character</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                                Gender
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("gender")}
                                    id="gender"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="non_binary">Non-binary</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                Status
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("status")}
                                    id="status"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="draft">Draft</option>
                                    <option value="pending_approval">Pending Approval</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="unsigned">Unsigned</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <div className="flex items-center h-full pt-6">
                                <input
                                    {...register("is_active")}
                                    id="is_active"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                                    Is Active
                                </label>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1"></div>


                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="birth_year" className="block text-sm font-medium text-gray-700">
                                Birth Year
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("birth_year", { valueAsNumber: true })}
                                    id="birth_year"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">N/A</option>
                                    {yearOptions.map((year: number) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="death_year" className="block text-sm font-medium text-gray-700">
                                Death Year
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("death_year", { valueAsNumber: true })}
                                    id="death_year"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">N/A</option>
                                    {yearOptions.map((year: number) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="cost_min" className="block text-sm font-medium text-gray-700">
                                Cost Min
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <span className="text-gray-500 sm:text-sm">$</span>
                                </div>
                                <input
                                    {...register("cost_min", { valueAsNumber: true })}
                                    type="number"
                                    id="cost_min"
                                    className="block w-full rounded-md border-gray-300 pl-7 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="cost_max" className="block text-sm font-medium text-gray-700">
                                Cost Max
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <span className="text-gray-500 sm:text-sm">$</span>
                                </div>
                                <input
                                    {...register("cost_max", { valueAsNumber: true })}
                                    type="number"
                                    id="cost_max"
                                    className="block w-full rounded-md border-gray-300 pl-7 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label htmlFor="primary_professions" className="block text-sm font-medium text-gray-700">
                                Primary Professions
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("primary_professions")}
                                    type="text"
                                    id="primary_professions"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label htmlFor="profile_image" className="block text-sm font-medium text-gray-700">
                                Profile Image URL
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("profile_image")}
                                    type="text"
                                    id="profile_image"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label htmlFor="hero_image" className="block text-sm font-medium text-gray-700">
                                Hero Image URL
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("hero_image")}
                                    type="text"
                                    id="hero_image"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <div className="mt-1">
                                <textarea
                                    {...register("description")}
                                    id="description"
                                    rows={4}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="Detailed description of the licensable property"
                                />
                            </div>
                        </div>

                    </div>
                </form>

                <div className="mt-8 bg-white shadow rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Social Accounts</h3>
                        <button
                            type="button"
                            onClick={() => appendSocial({ provider: "", external_id: "", link: "" })}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Add Account
                        </button>
                    </div>
                    <div className="space-y-4">
                        {socialFields.map((field, index) => (
                            <div key={field.id} className="flex gap-4 items-start border p-4 rounded-md bg-gray-50">
                                <div className="flex-1 space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <div>
                                            <label htmlFor={`social_accounts.${index}.provider`} className="block text-sm font-medium text-gray-700">Provider</label>
                                            <input
                                                {...register(`social_accounts.${index}.provider` as const)}
                                                placeholder="e.g. twitter"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`social_accounts.${index}.external_id`} className="block text-sm font-medium text-gray-700">Handle/ID</label>
                                            <input
                                                {...register(`social_accounts.${index}.external_id` as const)}
                                                placeholder="@handle"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`social_accounts.${index}.link`} className="block text-sm font-medium text-gray-700">Profile Link</label>
                                            <input
                                                {...register(`social_accounts.${index}.link` as const)}
                                                placeholder="https://..."
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeSocial(index)}
                                    className="text-red-600 hover:text-red-800 p-2"
                                >
                                    <span className="sr-only">Remove</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {socialFields.length === 0 && (
                            <p className="text-gray-500 text-sm italic">No social accounts added.</p>
                        )}
                    </div>
                </div>

                <div className="mt-8 bg-white shadow rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Characteristics</h3>
                        <button
                            type="button"
                            onClick={() => appendCharacteristic({ name: "", value: "" })}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Add Characteristic
                        </button>
                    </div>
                    <div className="space-y-4">
                        {characteristicFields.map((field, index) => (
                            <div key={field.id} className="flex gap-4 items-start border p-4 rounded-md bg-gray-50">
                                <div className="flex-1 space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor={`characteristics.${index}.name`} className="block text-sm font-medium text-gray-700">Name</label>
                                            <input
                                                {...register(`characteristics.${index}.name` as const)}
                                                placeholder="e.g. Eye Color"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`characteristics.${index}.value`} className="block text-sm font-medium text-gray-700">Value</label>
                                            <input
                                                {...register(`characteristics.${index}.value` as const)}
                                                placeholder="Blue"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeCharacteristic(index)}
                                    className="text-red-600 hover:text-red-800 p-2"
                                >
                                    <span className="sr-only">Remove</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {characteristicFields.length === 0 && (
                            <p className="text-gray-500 text-sm italic">No characteristics added.</p>
                        )}
                    </div>
                </div>

                <div className="mt-8 bg-white shadow rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Prices</h3>
                        <button
                            type="button"
                            onClick={() => appendPrice({ license_type: "personal", price: 0, currency: "USD" })}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Add Price
                        </button>
                    </div>
                    <div className="space-y-4">
                        {priceFields.map((field, index) => (
                            <div key={field.id} className="flex gap-4 items-start border p-4 rounded-md bg-gray-50">
                                <div className="flex-1 space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <div>
                                            <label htmlFor={`prices.${index}.license_type`} className="block text-sm font-medium text-gray-700">License Type</label>
                                            <select
                                                {...register(`prices.${index}.license_type` as const)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            >
                                                <option value="personal">Personal</option>
                                                <option value="commercial">Commercial</option>
                                                <option value="expanded">Expanded</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor={`prices.${index}.price`} className="block text-sm font-medium text-gray-700">Price</label>
                                            <div className="mt-1 relative rounded-md shadow-sm">
                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                </div>
                                                <input
                                                    {...register(`prices.${index}.price` as const, { valueAsNumber: true })}
                                                    type="number"
                                                    className="block w-full rounded-md border-gray-300 pl-7 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor={`prices.${index}.currency`} className="block text-sm font-medium text-gray-700">Currency</label>
                                            <select
                                                {...register(`prices.${index}.currency` as const)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            >
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                                <option value="GBP">GBP</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removePrice(index)}
                                    className="text-red-600 hover:text-red-800 p-2"
                                >
                                    <span className="sr-only">Remove</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {priceFields.length === 0 && (
                            <p className="text-gray-500 text-sm italic">No prices added.</p>
                        )}
                    </div>
                </div>
            </div>
        </AppContainer>
    );
};

export default LicensablePropertyEdit;
